import React, { useState, useCallback } from 'react';
import {
   Box,
   Card,
   CardContent,
   Typography,
   Avatar,
   Button,
   Chip,
   Stack,
   IconButton,
   Tooltip,
   Fade,
   CircularProgress,
   alpha,
} from '@mui/material';
import {
   CheckCircle as CheckCircleIcon,
   Cancel as CancelIcon,
   PhotoLibrary as PhotoLibraryIcon,
   Lock as LockIcon,
   Public as PublicIcon,
   OpenInNew as OpenInNewIcon,
   AccessTime as AccessTimeIcon,
   Person as PersonIcon,
   Schedule as ScheduleIcon,
   Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../../utils/auth';
import { formatDistanceToNow, isPast } from 'date-fns';
import { useUser } from '../../contexts/UserContext';

// Interface for the invitation data received by a user
export interface AlbumInvitationReceived {
   id: string; // Feed card ID
   invitationId: string;
   albumId: string;
   albumTitle: string;
   albumCoverUrl?: string;
   albumPhotoCount: number;
   albumIsPublic: boolean;
   senderName: string;
   senderEmail?: string;
   senderPhotoUrl?: string;
   role: 'viewer' | 'editor' | 'owner';
   status: 'pending' | 'accepted' | 'declined';
   sentAt: Date | any;
   expiresAt?: Date | any;
}

interface AlbumInvitationReceivedCardProps {
   invitation: AlbumInvitationReceived;
   onAcceptSuccess?: (invitationId: string) => void;
   onDeclineSuccess?: (invitationId: string) => void;
   onViewAlbum?: (albumId: string) => void;
}

// Helper function to convert Firebase timestamp to Date
const toDate = (timestamp: any): Date => {
   if (!timestamp) return new Date();
   if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
   }
   if (timestamp.seconds && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000);
   }
   if (timestamp._seconds && typeof timestamp._seconds === 'number') {
      return new Date(timestamp._seconds * 1000);
   }
   if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      return new Date(timestamp);
   }
   if (timestamp instanceof Date) {
      return timestamp;
   }
   return new Date(timestamp);
};

// Helper function to format relative time
const formatRelativeTime = (timestamp: any): string => {
   try {
      const date = toDate(timestamp);
      if (isNaN(date.getTime())) return 'Unknown';
      return formatDistanceToNow(date, { addSuffix: true });
   } catch {
      return 'Unknown';
   }
};

// Check if invitation is expired
const isExpired = (expiresAt: any): boolean => {
   if (!expiresAt) return false;
   try {
      const expDate = toDate(expiresAt);
      return isPast(expDate);
   } catch {
      return false;
   }
};

// Role configuration
const getRoleConfig = (role: 'viewer' | 'editor' | 'owner') => {
   switch (role) {
      case 'owner':
         return { label: 'Owner Access', color: '#dc2626', bgColor: '#fef2f2' };
      case 'editor':
         return { label: 'Editor Access', color: '#f59e0b', bgColor: '#fffbeb' };
      case 'viewer':
      default:
         return { label: 'View Only', color: '#3b82f6', bgColor: '#eff6ff' };
   }
};

const AlbumInvitationReceivedCard: React.FC<AlbumInvitationReceivedCardProps> = ({
   invitation,
   onAcceptSuccess,
   onDeclineSuccess,
   onViewAlbum,
}) => {
   const navigate = useNavigate();
   const { currentUser } = useUser();
   const [isAccepting, setIsAccepting] = useState(false);
   const [isDeclining, setIsDeclining] = useState(false);
   const [isVisible, setIsVisible] = useState(true);
   const [actionCompleted, setActionCompleted] = useState<'accepted' | 'declined' | null>(null);
   const [error, setError] = useState<string | null>(null);

   const expired = isExpired(invitation.expiresAt);
   const isButtonsDisabled = expired || invitation.status !== 'pending' || actionCompleted !== null;
   const roleConfig = getRoleConfig(invitation.role);

   // Helper function to generate token and process invitation action
   const processInvitationAction = useCallback(async (action: 'accept' | 'decline') => {
      const userEmail = currentUser?.email;
      if (!userEmail) {
         throw new Error(`Please log in to ${action} this invitation`);
      }

      // First, generate a token for the invitation
      const headers = await getAuthHeaders();
      const tokenResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/albums/generate-token`, {
         method: 'POST',
         headers,
         body: JSON.stringify({
            invitationId: invitation.invitationId,
            albumId: invitation.albumId,
            email: userEmail,
            type: 'album',
         }),
      });

      const tokenData = await tokenResponse.json().catch(() => ({}));
      
      if (!tokenResponse.ok || !tokenData.token) {
         throw new Error(tokenData.error || 'Failed to process invitation. Please try again.');
      }

      // Now use the public-accept endpoint (same as accept album public page)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/albums/public-accept`, {
         method: 'POST',
         headers: {
            'Content-Type': 'application/json',
         },
         body: JSON.stringify({
            token: tokenData.token,
            invitationId: invitation.invitationId,
            email: userEmail,
            action: action,
         }),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
         throw new Error(responseData.error || responseData.message || `Failed to ${action} invitation`);
      }

      return responseData;
   }, [invitation.invitationId, invitation.albumId, currentUser]);

   // Handle accept invitation
   const handleAccept = useCallback(async () => {
      if (isButtonsDisabled || isAccepting) return;

      setIsAccepting(true);
      setError(null);

      try {
         const responseData = await processInvitationAction('accept');

         // Check if already accepted
         if (responseData.alreadyAccepted) {
            setActionCompleted('accepted');
         } else {
            setActionCompleted('accepted');
         }
         
         // Fade out after showing success
         setTimeout(() => {
            setIsVisible(false);
            setTimeout(() => {
               onAcceptSuccess?.(invitation.invitationId);
            }, 300);
         }, 2000);
      } catch (err) {
         console.error('Error accepting invitation:', err);
         setError(err instanceof Error ? err.message : 'Failed to accept invitation');
         setTimeout(() => setError(null), 5000);
      } finally {
         setIsAccepting(false);
      }
   }, [invitation.invitationId, isButtonsDisabled, isAccepting, onAcceptSuccess, processInvitationAction]);

   // Handle decline invitation
   const handleDecline = useCallback(async () => {
      if (isButtonsDisabled || isDeclining) return;

      setIsDeclining(true);
      setError(null);

      try {
         await processInvitationAction('decline');

         setActionCompleted('declined');
         
         // Fade out after showing result
         setTimeout(() => {
            setIsVisible(false);
            setTimeout(() => {
               onDeclineSuccess?.(invitation.invitationId);
            }, 300);
         }, 2000);
      } catch (err) {
         console.error('Error declining invitation:', err);
         setError(err instanceof Error ? err.message : 'Failed to decline invitation');
         setTimeout(() => setError(null), 5000);
      } finally {
         setIsDeclining(false);
      }
   }, [invitation.invitationId, isButtonsDisabled, isDeclining, onDeclineSuccess, processInvitationAction]);

   // Handle view album - navigates to public album page
   const handleViewAlbum = useCallback(() => {
      // Navigate to public album page instead of album detail page
      navigate(`/albums/public/${invitation.albumId}`);
   }, [invitation.albumId, navigate]);

   return (
      <Fade in={isVisible} timeout={300}>
         <Card
            sx={{
               borderRadius: 2,
               boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
               border: '1px solid #e5e7eb',
               borderLeft: '4px solid #10b981', // Green left border for received invitations
               overflow: 'visible',
               background: expired
                  ? 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)'
                  : actionCompleted === 'accepted'
                  ? 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)'
                  : actionCompleted === 'declined'
                  ? 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)'
                  : 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 50%, #f0fdf4 100%)',
               transition: 'all 0.2s ease-in-out',
               opacity: expired ? 0.75 : 1,
               '&:hover': {
                  boxShadow: '0 4px 16px rgba(16, 185, 129, 0.15)',
                  transform: 'translateY(-1px)',
               },
            }}
         >
            <CardContent sx={{ p: 2.5 }}>
               {/* Header */}
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'flex-start',
                     mb: 2,
                  }}
               >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     <PersonIcon sx={{ color: '#10b981', fontSize: 20 }} />
                     <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151' }}>
                        Album Invitation
                     </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     {/* Role Badge */}
                     <Chip
                        label={roleConfig.label}
                        size="small"
                        sx={{
                           height: 24,
                           fontSize: '0.75rem',
                           fontWeight: 500,
                           backgroundColor: roleConfig.bgColor,
                           color: roleConfig.color,
                           border: `1px solid ${alpha(roleConfig.color, 0.3)}`,
                        }}
                     />

                     {/* Status Badge */}
                     {expired ? (
                        <Chip
                           icon={<WarningIcon sx={{ fontSize: 14 }} />}
                           label="Expired"
                           size="small"
                           sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              backgroundColor: '#fef2f2',
                              color: '#dc2626',
                           }}
                        />
                     ) : actionCompleted === 'accepted' ? (
                        <Chip
                           icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                           label="Accepted"
                           size="small"
                           sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              backgroundColor: '#ecfdf5',
                              color: '#10b981',
                           }}
                        />
                     ) : actionCompleted === 'declined' ? (
                        <Chip
                           icon={<CancelIcon sx={{ fontSize: 14 }} />}
                           label="Declined"
                           size="small"
                           sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              backgroundColor: '#fef2f2',
                              color: '#dc2626',
                           }}
                        />
                     ) : (
                        <Chip
                           icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                           label="Pending"
                           size="small"
                           sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              backgroundColor: '#eff6ff',
                              color: '#3b82f6',
                           }}
                        />
                     )}
                  </Box>
               </Box>

               {/* Sender Information */}
               <Box
                  sx={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: 2,
                     p: 1.5,
                     mb: 2,
                     backgroundColor: '#f9fafb',
                     borderRadius: 1.5,
                     border: '1px solid #e5e7eb',
                  }}
               >
                  <Avatar
                     src={invitation.senderPhotoUrl}
                     sx={{
                        width: 48,
                        height: 48,
                        bgcolor: '#10b981',
                        fontSize: '1.2rem',
                        fontWeight: 600,
                     }}
                  >
                     {invitation.senderName ? invitation.senderName[0].toUpperCase() : 'U'}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                     <Typography
                        variant="subtitle1"
                        sx={{
                           fontWeight: 600,
                           color: '#111827',
                        }}
                     >
                        {invitation.senderName || 'Someone'}
                     </Typography>
                     <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        invited you to join an album
                     </Typography>
                  </Box>
               </Box>

               {/* Album Information */}
               <Box
                  sx={{
                     display: 'flex',
                     gap: 2,
                     mb: 2,
                     p: 1.5,
                     backgroundColor: '#ffffff',
                     borderRadius: 1.5,
                     border: '1px solid #e5e7eb',
                  }}
               >
                  {/* Album Thumbnail */}
                  <Box
                     sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 1,
                        overflow: 'hidden',
                        flexShrink: 0,
                        backgroundColor: '#e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                     }}
                  >
                     {invitation.albumCoverUrl ? (
                        <Box
                           component="img"
                           src={invitation.albumCoverUrl}
                           alt={invitation.albumTitle}
                           sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                           }}
                        />
                     ) : (
                        <PhotoLibraryIcon sx={{ fontSize: 28, color: '#9ca3af' }} />
                     )}
                  </Box>

                  {/* Album Details */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                     <Typography
                        variant="subtitle2"
                        sx={{
                           fontWeight: 600,
                           color: '#111827',
                           mb: 0.5,
                        }}
                     >
                        {invitation.albumTitle}
                     </Typography>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <PhotoLibraryIcon sx={{ fontSize: 14, color: '#6b7280' }} />
                           <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              {invitation.albumPhotoCount} {invitation.albumPhotoCount === 1 ? 'photo' : 'photos'}
                           </Typography>
                        </Box>
                        <Chip
                           icon={
                              invitation.albumIsPublic ? (
                                 <PublicIcon sx={{ fontSize: 14 }} />
                              ) : (
                                 <LockIcon sx={{ fontSize: 14 }} />
                              )
                           }
                           label={invitation.albumIsPublic ? 'Public' : 'Private'}
                           size="small"
                           sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              backgroundColor: invitation.albumIsPublic ? '#ecfdf5' : '#f3f4f6',
                              color: invitation.albumIsPublic ? '#10b981' : '#6b7280',
                              border: 'none',
                           }}
                        />
                     </Box>
                  </Box>
               </Box>

               {/* Timing Information */}
               <Box
                  sx={{
                     display: 'flex',
                     alignItems: 'center',
                     mb: 2,
                  }}
               >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                     <AccessTimeIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                     <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        Received {formatRelativeTime(invitation.sentAt)}
                     </Typography>
                  </Box>
               </Box>

               {/* Error Message */}
               {error && (
                  <Fade in={!!error}>
                     <Box
                        sx={{
                           display: 'flex',
                           alignItems: 'center',
                           gap: 1,
                           p: 1.5,
                           mb: 2,
                           backgroundColor: '#fef2f2',
                           borderRadius: 1,
                           border: '1px solid #fecaca',
                        }}
                     >
                        <WarningIcon sx={{ color: '#dc2626', fontSize: 18 }} />
                        <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 500 }}>
                           {error}
                        </Typography>
                     </Box>
                  </Fade>
               )}

               {/* Action Buttons */}
               {!actionCompleted && !expired && (
                  <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1 }}>
                     {/* Accept Button */}
                     <Button
                        variant="contained"
                        size="small"
                        startIcon={isAccepting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                        onClick={handleAccept}
                        disabled={isButtonsDisabled || isAccepting || isDeclining}
                        sx={{
                           flex: 1,
                           minWidth: 120,
                           backgroundColor: '#10b981',
                           color: '#ffffff',
                           fontWeight: 600,
                           textTransform: 'none',
                           borderRadius: 1.5,
                           py: 1,
                           '&:hover': {
                              backgroundColor: '#059669',
                           },
                           '&.Mui-disabled': {
                              backgroundColor: '#e5e7eb',
                              color: '#9ca3af',
                           },
                        }}
                     >
                        {isAccepting ? 'Accepting...' : 'Accept'}
                     </Button>

                     {/* Decline Button */}
                     <Button
                        variant="outlined"
                        size="small"
                        startIcon={isDeclining ? <CircularProgress size={16} /> : <CancelIcon />}
                        onClick={handleDecline}
                        disabled={isButtonsDisabled || isAccepting || isDeclining}
                        sx={{
                           flex: 1,
                           minWidth: 120,
                           borderColor: '#dc2626',
                           color: '#dc2626',
                           fontWeight: 600,
                           textTransform: 'none',
                           borderRadius: 1.5,
                           py: 1,
                           '&:hover': {
                              backgroundColor: '#fef2f2',
                              borderColor: '#dc2626',
                           },
                           '&.Mui-disabled': {
                              borderColor: '#e5e7eb',
                              color: '#9ca3af',
                           },
                        }}
                     >
                        {isDeclining ? 'Declining...' : 'Decline'}
                     </Button>

                     {/* View Album Link */}
                     <Tooltip title="View Album">
                        <IconButton
                           size="small"
                           onClick={handleViewAlbum}
                           sx={{
                              color: '#6b7280',
                              '&:hover': {
                                 backgroundColor: '#f3f4f6',
                                 color: '#10b981',
                              },
                           }}
                        >
                           <OpenInNewIcon fontSize="small" />
                        </IconButton>
                     </Tooltip>
                  </Stack>
               )}

               {/* Action Completed Message */}
               {actionCompleted && (
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        p: 1.5,
                        backgroundColor: actionCompleted === 'accepted' ? '#ecfdf5' : '#fef2f2',
                        borderRadius: 1,
                     }}
                  >
                     {actionCompleted === 'accepted' ? (
                        <>
                           <CheckCircleIcon sx={{ color: '#10b981' }} />
                           <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 600 }}>
                              Invitation accepted! You now have access to this album.
                           </Typography>
                        </>
                     ) : (
                        <>
                           <CancelIcon sx={{ color: '#dc2626' }} />
                           <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 600 }}>
                              Invitation declined.
                           </Typography>
                        </>
                     )}
                  </Box>
               )}
            </CardContent>
         </Card>
      </Fade>
   );
};

export default AlbumInvitationReceivedCard;


